/**
 * ObstacleSidePanel — Side panel for obstacle create + edit in view mode
 *
 * TWO MODES:
 * A) Edit mode: renders ObstaclePropertiesPanel with close icon
 * B) Create mode: shows creation form with defaults, ghost follows cursor
 *
 * Rendered as an absolute overlay on the right edge of the map.
 */

import { useState, useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import { useObstacleStore, type CreateObstacleDefaults } from '@/stores/obstacleStore'
import { ObstaclePropertiesPanel } from './ObstaclePropertiesPanel'
import {
  OBSTACLE_TYPE_LABELS,
  OBSTACLE_MOBILITY_LABELS,
  type ObstacleType,
  type ObstacleMobility,
  type ObstacleShape,
  type Obstacle,
} from '@/types/obstacle'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface ObstacleSidePanelProps {
  /** Existing obstacle for edit mode (null = create mode) */
  obstacle?: Obstacle | null
  onClose: () => void
}

const OBSTACLE_TYPES: ObstacleType[] = [
  'column', 'wall', 'firewall', 'office', 'storage',
  'restroom', 'equipment', 'vehicle', 'staging',
  'barrier', 'utility', 'other',
]

const SHAPES: ObstacleShape[] = ['rectangle', 'circle']
const MOBILITIES: ObstacleMobility[] = ['static', 'semi_permanent', 'dynamic']

export function ObstacleSidePanel({ obstacle, onClose }: ObstacleSidePanelProps) {
  const isCreatingMode = useObstacleStore((s) => s.isCreatingMode)
  const creatingDefaults = useObstacleStore((s) => s.creatingDefaults)
  const cancelCreating = useObstacleStore((s) => s.cancelCreating)
  const setCreatingDefaults = useObstacleStore((s) => s.setCreatingDefaults)

  // Determine mode
  const isCreateMode = isCreatingMode && !obstacle

  // Escape key dismisses
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isCreateMode) {
          cancelCreating()
        }
        onClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, isCreateMode, cancelCreating])

  if (isCreateMode && creatingDefaults) {
    return (
      <div
        className="absolute right-0 top-0 z-30 w-[420px] border-l bg-background flex flex-col h-full overflow-hidden shadow-lg"
        onWheel={(e) => e.stopPropagation()}
      >
        <CreateObstacleForm
          defaults={creatingDefaults}
          onChange={setCreatingDefaults}
          onCancel={() => {
            cancelCreating()
            onClose()
          }}
        />
      </div>
    )
  }

  if (obstacle) {
    return (
      <div
        className="absolute right-0 top-0 z-30 w-[420px] border-l bg-background flex flex-col h-full overflow-hidden shadow-lg"
        onWheel={(e) => e.stopPropagation()}
      >
        <ObstaclePropertiesPanel
          obstacle={obstacle}
          onBack={onClose}
          closeIcon="close"
        />
      </div>
    )
  }

  return null
}

// --------------------------------------------------------------------------
// Create mode form
// --------------------------------------------------------------------------

interface CreateObstacleFormProps {
  defaults: CreateObstacleDefaults
  onChange: (d: CreateObstacleDefaults) => void
  onCancel: () => void
}

function CreateObstacleForm({ defaults, onChange, onCancel }: CreateObstacleFormProps) {
  // Local state mirrors store defaults for responsive UI
  const [form, setForm] = useState<CreateObstacleDefaults>(defaults)

  // Sync local → store on every change
  const update = useCallback(
    (patch: Partial<CreateObstacleDefaults>) => {
      setForm((prev) => {
        const next = { ...prev, ...patch }
        onChange(next)
        return next
      })
    },
    [onChange]
  )

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium truncate">New Obstacle</span>
          <span className="text-[10px] text-muted-foreground">Click map to place</span>
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-5">
          {/* Identity */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Identity</h4>

            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                className="h-8 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Type</Label>
                <Select
                  value={form.obstacleType}
                  onValueChange={(v) => update({ obstacleType: v as ObstacleType })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OBSTACLE_TYPES.map((t) => (
                      <SelectItem key={t} value={t} className="text-xs">
                        {OBSTACLE_TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Mobility</Label>
                <Select
                  value={form.mobility}
                  onValueChange={(v) => update({ mobility: v as ObstacleMobility })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MOBILITIES.map((m) => (
                      <SelectItem key={m} value={m} className="text-xs">
                        {OBSTACLE_MOBILITY_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Shape</Label>
              <Select
                value={form.shape}
                onValueChange={(v) => update({ shape: v as ObstacleShape })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SHAPES.map((s) => (
                    <SelectItem key={s} value={s} className="text-xs capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          <Separator />

          {/* Dimensions */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Dimensions (ft)</h4>

            {form.shape === 'rectangle' ? (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Width</Label>
                  <Input
                    type="number"
                    value={form.width}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value)
                      if (!isNaN(v) && v > 0) update({ width: v })
                    }}
                    className="h-8 text-sm"
                    min={0.5}
                    step={0.5}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Depth</Label>
                  <Input
                    type="number"
                    value={form.depth}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value)
                      if (!isNaN(v) && v > 0) update({ depth: v })
                    }}
                    className="h-8 text-sm"
                    min={0.5}
                    step={0.5}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">Radius</Label>
                <Input
                  type="number"
                  value={form.radius}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value)
                    if (!isNaN(v) && v > 0) update({ radius: v })
                  }}
                  className="h-8 text-sm"
                  min={0.25}
                  step={0.25}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Height</Label>
                <Input
                  type="number"
                  value={form.height}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value)
                    if (!isNaN(v) && v > 0) update({ height: v })
                  }}
                  className="h-8 text-sm"
                  min={0.1}
                  step={0.5}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Elevation</Label>
                <Input
                  type="number"
                  value={form.elevation}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value)
                    if (!isNaN(v)) update({ elevation: v })
                  }}
                  className="h-8 text-sm"
                  min={0}
                  step={0.5}
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* Collision */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Collision</h4>

            <div className="flex items-center justify-between">
              <Label className="text-xs">Participates in collision</Label>
              <Switch
                checked={form.participatesInCollision}
                onCheckedChange={(v) => update({ participatesInCollision: v })}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Clearance buffer (ft)</Label>
              <Input
                type="number"
                value={form.clearanceBufferFt}
                onChange={(e) => {
                  const v = parseFloat(e.target.value)
                  if (!isNaN(v)) update({ clearanceBufferFt: v })
                }}
                className="h-8 text-sm"
                min={0}
                step={0.5}
              />
            </div>
          </section>

          <Separator />

          {/* Appearance */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Appearance</h4>

            <div className="flex items-center gap-3">
              <Label className="text-xs">Color</Label>
              <input
                type="color"
                value={form.color}
                onChange={(e) => update({ color: e.target.value })}
                className="w-8 h-8 rounded border cursor-pointer"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Opacity ({Math.round(form.opacity * 100)}%)</Label>
              <Slider
                value={[form.opacity]}
                min={0}
                max={1}
                step={0.05}
                onValueChange={([v]) => update({ opacity: v })}
              />
            </div>
          </section>

          <Separator />

          {/* Cancel */}
          <Button variant="outline" size="sm" className="w-full" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </ScrollArea>
    </div>
  )
}
